/**
 * Sendblue Tags/Groups API Integration
 * Provides functionality to manage contact tags via Sendblue Groups API
 * 
 * Sendblue uses "Groups" to organize contacts - we expose these as "Tags" in the UI
 */

import { collection, doc, getDocs, setDoc, deleteDoc, onSnapshot, Unsubscribe, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { crmAuthedFetch } from './crmAuthedFetch';

const CLOUD_FUNCTION_BASE = 'https://us-central1-lctf-projects.cloudfunctions.net';
const SENDBLUE_TAGS_COLLECTION = 'sendblueTags';
const CONTACT_TAGS_COLLECTION = 'contactSendbleTags';

// Types
export interface SendblueGroup {
  id: string;
  name: string;
  color: string;
  memberCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface ContactTag {
  contactPhone: string;
  tagId: string;
  tagName: string;
  addedAt: Date;
  addedBy: string;
}

export interface SendblueTagsResponse {
  success: boolean;
  groups?: SendblueGroup[];
  error?: string;
}

export interface SendblueTagResponse {
  success: boolean;
  group?: SendblueGroup;
  error?: string;
}

// Predefined tag colors for a nice visual palette
const TAG_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#84CC16', // Lime
  '#22C55E', // Green
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#EC4899', // Pink
];

/**
 * Get a random color from the palette
 */
export function getRandomTagColor(): string {
  return TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
}

/**
 * Fetch all Sendblue groups (tags) via Cloud Function
 */
export async function fetchSendblueGroups(): Promise<SendblueTagsResponse> {
  try {
    const response = await crmAuthedFetch(`${CLOUD_FUNCTION_BASE}/getSendblueGroups`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Failed to fetch groups: ${response.status}`,
      };
    }

    const data = await response.json();
    return {
      success: true,
      groups: data.groups || [],
    };
  } catch (error) {
    console.error('Error fetching Sendblue groups:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch groups',
    };
  }
}

/**
 * Create a new Sendblue group (tag) via Cloud Function
 */
export async function createSendblueGroup(name: string, color?: string): Promise<SendblueTagResponse> {
  try {
    const response = await crmAuthedFetch(`${CLOUD_FUNCTION_BASE}/createSendblueGroup`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        color: color || getRandomTagColor(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Failed to create group: ${response.status}`,
      };
    }

    const data = await response.json();
    return {
      success: true,
      group: data.group,
    };
  } catch (error) {
    console.error('Error creating Sendblue group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create group',
    };
  }
}

/**
 * Delete a Sendblue group (tag) via Cloud Function
 */
export async function deleteSendblueGroup(groupId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await crmAuthedFetch(`${CLOUD_FUNCTION_BASE}/deleteSendblueGroup`, {
      method: 'POST',
      body: JSON.stringify({ groupId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Failed to delete group: ${response.status}`,
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting Sendblue group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete group',
    };
  }
}

/**
 * Add a contact to a Sendblue group (tag) via Cloud Function
 */
export async function addContactToGroup(
  groupId: string,
  phoneNumber: string,
  userId: string,
  tagName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await crmAuthedFetch(`${CLOUD_FUNCTION_BASE}/addContactToSendblueGroup`, {
      method: 'POST',
      body: JSON.stringify({
        groupId,
        phoneNumber,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Failed to add contact to group: ${response.status}`,
      };
    }

    // Also store the relationship in Firestore for quick lookup
    const normalizedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
    await setDoc(doc(db, CONTACT_TAGS_COLLECTION, `${normalizedPhone}_${groupId}`), {
      contactPhone: normalizedPhone,
      tagId: groupId,
      tagName,
      addedAt: new Date(),
      addedBy: userId,
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding contact to Sendblue group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to add contact to group',
    };
  }
}

/**
 * Remove a contact from a Sendblue group (tag) via Cloud Function
 */
export async function removeContactFromGroup(
  groupId: string,
  phoneNumber: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await crmAuthedFetch(`${CLOUD_FUNCTION_BASE}/removeContactFromSendblueGroup`, {
      method: 'POST',
      body: JSON.stringify({
        groupId,
        phoneNumber,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `Failed to remove contact from group: ${response.status}`,
      };
    }

    // Also remove from Firestore
    const normalizedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
    await deleteDoc(doc(db, CONTACT_TAGS_COLLECTION, `${normalizedPhone}_${groupId}`));

    return { success: true };
  } catch (error) {
    console.error('Error removing contact from Sendblue group:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to remove contact from group',
    };
  }
}

/**
 * Get tags for a specific contact from local Firestore cache
 */
export async function getTagsForContact(phoneNumber: string): Promise<ContactTag[]> {
  try {
    const normalizedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
    const tagsRef = collection(db, CONTACT_TAGS_COLLECTION);
    const snapshot = await getDocs(tagsRef);
    
    const tags: ContactTag[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.contactPhone === normalizedPhone) {
        tags.push({
          contactPhone: data.contactPhone,
          tagId: data.tagId,
          tagName: data.tagName,
          addedAt: data.addedAt?.toDate?.() || new Date(),
          addedBy: data.addedBy,
        });
      }
    });
    
    return tags;
  } catch (error) {
    console.error('Error getting tags for contact:', error);
    return [];
  }
}

/**
 * Subscribe to tags for a specific contact (real-time updates)
 */
export function subscribeToContactTags(
  phoneNumber: string,
  onTagsUpdate: (tags: ContactTag[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const normalizedPhone = phoneNumber.replace(/\D/g, '').slice(-10);
  const tagsRef = collection(db, CONTACT_TAGS_COLLECTION);
  
  return onSnapshot(
    tagsRef,
    (snapshot) => {
      const tags: ContactTag[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.contactPhone === normalizedPhone) {
          tags.push({
            contactPhone: data.contactPhone,
            tagId: data.tagId,
            tagName: data.tagName,
            addedAt: data.addedAt?.toDate?.() || new Date(),
            addedBy: data.addedBy,
          });
        }
      });
      onTagsUpdate(tags);
    },
    (error) => {
      console.error('Error in contact tags subscription:', error);
      onError?.(error);
    }
  );
}

/**
 * Get all available tags from Firestore cache (for quick UI loading)
 * Falls back to API call if cache is empty
 */
export async function getAllTags(): Promise<SendblueGroup[]> {
  try {
    const tagsRef = collection(db, SENDBLUE_TAGS_COLLECTION);
    const q = query(tagsRef, orderBy('name', 'asc'));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      // Cache is empty, fetch from API
      const result = await fetchSendblueGroups();
      if (result.success && result.groups) {
        // Cache the results
        for (const group of result.groups) {
          await setDoc(doc(db, SENDBLUE_TAGS_COLLECTION, group.id), {
            ...group,
            cachedAt: new Date(),
          });
        }
        return result.groups;
      }
      return [];
    }
    
    const tags: SendblueGroup[] = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        color: data.color || getRandomTagColor(),
        memberCount: data.memberCount || 0,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      };
    });
    
    return tags;
  } catch (error) {
    console.error('Error getting all tags:', error);
    return [];
  }
}

/**
 * Subscribe to all tags (real-time updates)
 */
export function subscribeToAllTags(
  onTagsUpdate: (tags: SendblueGroup[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const tagsRef = collection(db, SENDBLUE_TAGS_COLLECTION);
  const q = query(tagsRef, orderBy('name', 'asc'));
  
  return onSnapshot(
    q,
    (snapshot) => {
      const tags: SendblueGroup[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          color: data.color || getRandomTagColor(),
          memberCount: data.memberCount || 0,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        };
      });
      onTagsUpdate(tags);
    },
    (error) => {
      console.error('Error in tags subscription:', error);
      onError?.(error);
    }
  );
}

/**
 * Refresh tags cache from Sendblue API
 */
export async function refreshTagsCache(): Promise<{ success: boolean; count: number; error?: string }> {
  try {
    const result = await fetchSendblueGroups();
    
    if (!result.success || !result.groups) {
      return {
        success: false,
        count: 0,
        error: result.error || 'Failed to fetch groups from Sendblue',
      };
    }
    
    // Update cache
    for (const group of result.groups) {
      await setDoc(doc(db, SENDBLUE_TAGS_COLLECTION, group.id), {
        ...group,
        cachedAt: new Date(),
      });
    }
    
    return {
      success: true,
      count: result.groups.length,
    };
  } catch (error) {
    console.error('Error refreshing tags cache:', error);
    return {
      success: false,
      count: 0,
      error: error instanceof Error ? error.message : 'Failed to refresh cache',
    };
  }
}

/**
 * Save a locally-created tag to Firestore (for when Sendblue API creates it)
 */
export async function saveTagToCache(tag: SendblueGroup): Promise<void> {
  try {
    await setDoc(doc(db, SENDBLUE_TAGS_COLLECTION, tag.id), {
      ...tag,
      cachedAt: new Date(),
    });
  } catch (error) {
    console.error('Error saving tag to cache:', error);
  }
}

/**
 * Remove a tag from Firestore cache
 */
export async function removeTagFromCache(tagId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, SENDBLUE_TAGS_COLLECTION, tagId));
  } catch (error) {
    console.error('Error removing tag from cache:', error);
  }
}
